import { useState } from "react";
import { createRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ClubGuard } from "@/components/ClubGuard";
import { useClubChannel } from "@/lib/clubs/useClubChannel";
import {
  gatheringDetailKey,
  gatheringsListKey,
  useClubDetails,
  useClubGatherings,
} from "@/lib/clubs/gatherings";
import {
  GatheringsTimeline,
  type StatusFilter,
} from "@/components/gatherings/GatheringsTimeline";
import { NotLocalClubNotice } from "@/components/gatherings/NotLocalClubNotice";
import { clubsRoute } from "./clubs";

function GatheringsList() {
  const params = useParams({ from: clubsGatheringsRoute.id });
  const clubId = params.id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>("all");

  const { data: club, isLoading: clubLoading } = useClubDetails(clubId);
  const { data, isLoading } = useClubGatherings(clubId, { upcoming: false });

  useClubChannel(clubId, (event) => {
    if (
      event.type === "gathering:updated" ||
      event.type === "gathering:cancelled"
    ) {
      qc.invalidateQueries({ queryKey: gatheringsListKey(clubId) });
      qc.invalidateQueries({
        queryKey: gatheringDetailKey(clubId, event.payload.gatheringId),
      });
      return;
    }
    if (event.type === "gathering:rsvp_updated") {
      qc.invalidateQueries({ queryKey: gatheringsListKey(clubId) });
      qc.invalidateQueries({
        queryKey: gatheringDetailKey(clubId, event.payload.gatheringId),
      });
    }
  });

  if ((isLoading && !data) || (clubLoading && !club)) {
    return (
      <div role="status" aria-live="polite" style={{ padding: 32 }}>
        Loading…
      </div>
    );
  }

  if (club && !club.isLocalClub) {
    return <NotLocalClubNotice clubName={club.name} />;
  }

  const gatherings = data?.gatherings ?? [];

  return (
    <GatheringsTimeline
      gatherings={gatherings}
      filter={filter}
      onFilterChange={setFilter}
      totalCount={gatherings.length}
      onSchedule={() =>
        navigate({ to: "/clubs/$id/gatherings/new", params: { id: clubId } })
      }
      onSelect={(gid) =>
        navigate({
          to: "/clubs/$id/gatherings/$gid",
          params: { id: clubId, gid },
        })
      }
    />
  );
}

export const clubsGatheringsRoute = createRoute({
  getParentRoute: () => clubsRoute,
  path: "/$id/gatherings",
  component: () => (
    <ClubGuard requireClubId>
      <GatheringsList />
    </ClubGuard>
  ),
});
